import { Message } from '@/services/types/artifact-types';

/**
 * Returns the sender (role === 'from') or first recipient in recipient_blob.
 */
export const getSender = (message: Message) => {
  if (!message.recipient_blob?.recipients?.length) return null;
  const sender = message.recipient_blob.recipients.find(
    (r) => r.role === 'from',
  );
  return sender || message.recipient_blob.recipients[0];
};

/**
 * Builds the sender's initials for avatar display.
 */
export const getSenderInitials = (
  sender: { name?: string | null; handle?: string | null } | null,
) => {
  if (!sender) return '';
  const name = sender.name || sender.handle || '';
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

/**
 * Groups message recipients by their role (excluding the sender).
 */
export const getRecipientsByRole = (message: Message) => {
  if (!message.recipient_blob?.recipients?.length) return {};
  const recipients = message.recipient_blob.recipients.filter(
    (r) => r.role !== 'from',
  );
  return recipients.reduce(
    (acc, recipient) => {
      const role = recipient.role || 'unknown';
      if (!acc[role]) acc[role] = [];
      acc[role].push(recipient);
      return acc;
    },
    {} as Record<string, typeof message.recipient_blob.recipients>,
  );
};

/**
 * Builds a collapsed, single-line recipient summary like "To: Alice • CC: Bob".
 */
export const buildRecipientSummary = (message: Message) => {
  const recipientsByRole = getRecipientsByRole(message);
  const allRecipients = Object.values(recipientsByRole).flat();
  if (allRecipients.length === 0) return null;
  if (allRecipients.length === 1) {
    const recipient = allRecipients[0];
    return recipient.name || recipient.handle || 'Unknown';
  }

  const roleOrder = ['to', 'cc', 'bcc', 'unknown'];
  const summaryParts: string[] = [];
  roleOrder.forEach((role) => {
    const recipients = recipientsByRole[role];
    if (recipients?.length) {
      const names = recipients.map((r) => r.name || r.handle).join(', ');
      const roleLabel = role.charAt(0).toUpperCase() + role.slice(1);
      summaryParts.push(`${roleLabel}: ${names}`);
    }
  });

  return summaryParts.join(' • ');
};
